import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Row, Alert } from 'react-bootstrap';
import { IInvoice } from '../../../model/invoice/Invoices';
import { InvoiceService } from '../InvoicesService';
import CustomFancybox from '../../../shared/components/Fancybox';
import { formatDate, getCorrectEnums } from '../../../utils/Functions';
import ModalInvoiceReject from './ModalInvoiceReject';
import ModalInvoiceConfirm from './ModalInvoiceConfirm';
import { APPROVED_INVOICE, REJECTED_INVOICE } from '../../../enum/Messages';
import { priceSplitter } from '../../../utils/helpers/NumberFormater';
import { ErrorToast } from '../../../utils/toasters';

export function InvoiceForm() {
    const { id } = useParams<{ id: string }>();

    const [invoice, setInvoice] = useState<IInvoice | null>(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    const [modalData, setModalData] = useState({ showModal: false });
    const [confirmModalData, setConfirmModalData] = useState({ showModal: false });

    useEffect(() => {
        if (!id) return;

        setLoading(true);
        setNotFound(false);

        InvoiceService.getInvoice(Number(id))
            .then(data => {
                if (!data?.id) {
                    setNotFound(true);
                    setInvoice(null);
                } else {
                    setInvoice(data);
                }
            })
            .catch(err => {
                if (err?.response?.status === 404) {
                    setNotFound(true);
                } else {
                    ErrorToast(err);
                }
            })
            .finally(() => setLoading(false));
    }, [id]);

    const rejectInvoice = (updatedInvoice: IInvoice) => {
        if (!invoice) return;

        InvoiceService.rejectInvoice(Number(id), updatedInvoice)
            .then(data => {
                setInvoice(data);
                toast(REJECTED_INVOICE);
                setModalData(prev => ({ ...prev, showModal: false }));
            })
            .catch(err => ErrorToast(err));
    };

    const verifyInvoice = () => {
        if (!invoice) return;

        InvoiceService.verifyInvoice(Number(id))
            .then(data => {
                setInvoice(data);
                toast(APPROVED_INVOICE);
                setConfirmModalData(prev => ({ ...prev, showModal: false }));
            })
            .catch(err => ErrorToast(err));
    };

    /* =====================
        STATES
    ====================== */

    if (loading) {
        return (
            <Card className="p-5 text-center">
                <h4>Loading invoice…</h4>
            </Card>
        );
    }

    if (notFound || !invoice) {
        return (
            <Card className="p-5 text-center">
                <Alert variant="outline-primary">
                    <h4>No invoice found</h4>
                    <p className="mb-0">The invoice you are trying to access does not exist or was removed.</p>
                </Alert>
            </Card>
        );
    }

    /* =====================
        ORIGINAL FORM
    ====================== */

    return (
        <>
            <ModalInvoiceReject
                {...modalData}
                setModalData={setModalData}
                updateInvoice={rejectInvoice}
                invoice={invoice}
            />
            <ModalInvoiceConfirm
                {...confirmModalData}
                setModalData={setConfirmModalData}
                updateInvoice={verifyInvoice}
            />

            <div>
                <h3 className="col-8 mb-0">Invoice ID: {invoice.id}</h3>
            </div>

            <Card className="mb-5">
                <Form as={Card.Body}>
                    <Row className="d-flex justify-content-between">
                        <Col md={4}>
                            {invoice.contract && <span className="status long">long-term</span>}

                            {!invoice.contract && (
                                <span
                                    className={`status ${
                                        invoice.isLongTerm ? 'linked' : invoice.offer?.isAddonsOnly ? 'negotiate' : ''
                                    }`}
                                >
                                    {invoice.isLongTerm
                                        ? 'linked'
                                        : invoice.offer?.isAddonsOnly
                                        ? 'add-ons'
                                        : 'standard'}
                                </span>
                            )}

                            <h4 className="mt-4">
                                <strong>Campaign:</strong>
                                <span className="text-primary">({invoice.campaign?.id})</span>
                                {invoice.campaign?.title}
                            </h4>

                            <h4>
                                <strong>Influencer:</strong>
                                <span className="text-primary">({invoice.user?.id})</span>
                                {invoice.user?.displayName}
                            </h4>

                            {invoice.user?.locale && (
                                <h4>
                                    <strong>Influencer language:</strong>{' '}
                                    {invoice.user.locale === 'de' ? 'German' : 'English'}
                                </h4>
                            )}

                            <h4>
                                <strong>Upload date:</strong> {formatDate(invoice.date)}
                            </h4>

                            {invoice.offer?.id && (
                                <h4>
                                    <strong>Offer ID:</strong> {invoice.offer.id}
                                </h4>
                            )}

                            {invoice.contract?.id && (
                                <h4>
                                    <strong>Contract ID:</strong> {invoice.contract.id}
                                </h4>
                            )}

                            {invoice.contractInvoiceSchedule?.id && (
                                <h4>
                                    <strong>Invoice position:</strong> {invoice.contractInvoiceSchedule.sequenceNumber}/
                                    {invoice.contract?.possibleInvoices}
                                </h4>
                            )}

                            {invoice.contract?.id ? (
                                <h4>
                                    <strong>Influencer payout:</strong>{' '}
                                    {priceSplitter(invoice.contractInvoiceSchedule?.price ?? 0)}€
                                </h4>
                            ) : (
                                <h4>
                                    <strong>Influencer payout:</strong>{' '}
                                    {invoice.offer?.influencerDeducted
                                        ? priceSplitter(invoice.offer.influencerDeducted)
                                        : invoice.isLongTerm
                                        ? priceSplitter(invoice.offer?.spendingPrice)
                                        : priceSplitter(invoice.offer?.price)}
                                    €
                                    {invoice.offer?.additionalPrice > 0 &&
                                        `(+${priceSplitter(invoice.offer.additionalPrice)})€`}
                                </h4>
                            )}

                            {invoice.offer?.hasInfluencerFee && (
                                <h4>
                                    <strong>Influencer fee:</strong>{' '}
                                    {priceSplitter(invoice.offer.price - invoice.offer.influencerDeducted)}€
                                    <span className="text-primary">({invoice.offer.influencerFeePercentage}%)</span>
                                </h4>
                            )}

                            {!invoice.contract && (
                                <h4>
                                    <strong>Total:</strong>{' '}
                                    {invoice.isLongTerm
                                        ? priceSplitter(
                                              Math.round(invoice.offer?.spendingPrice / invoice.campaign?.feePercentage)
                                          )
                                        : priceSplitter(
                                              Math.round(
                                                  invoice.offer?.price / invoice.campaign?.feePercentage +
                                                      invoice.offer?.additionalPrice
                                              )
                                          )}
                                    €
                                </h4>
                            )}

                            {(invoice.offer?.paymentTerms || invoice.campaign?.paymentTerms) && (
                                <h4>
                                    <strong>Payment terms:</strong>{' '}
                                    {getCorrectEnums(invoice.offer?.paymentTerms ?? invoice.campaign?.paymentTerms)}
                                </h4>
                            )}

                            <h4>
                                <strong>Company:</strong> {invoice.campaign?.company?.displayName}
                            </h4>

                            <h4>
                                <strong>Campaign managers:</strong>{' '}
                                {invoice.campaign?.assignedUsers
                                    ?.map((u: any) => `(${u.id}) ${u.firstName} ${u.lastName}`)
                                    .join(', ')}
                            </h4>

                            {invoice.user?.agencyCompany?.displayName && (
                                <h4>
                                    <strong>Agency:</strong> {invoice.user.agencyCompany.displayName}
                                </h4>
                            )}

                            {invoice.isVerified && !invoice.isPaid && (
                                <h4 className="text-primary">Invoice Verified</h4>
                            )}

                            {invoice.isPaid && (
                                <h4 className="text-primary">Invoice Paid on {formatDate(invoice?.paidAt ?? '-')}</h4>
                            )}

                            {invoice.isRejected && invoice.rejectReason && (
                                <h4 className="text-danger">
                                    <strong>Reject reason:</strong> {invoice.rejectReason}
                                </h4>
                            )}

                            {invoice?.offer?.purchaseOrderInfluencer && (
                                <h4>
                                    <strong>Influencer PO:</strong> {invoice?.offer?.purchaseOrderInfluencer ?? '-'}
                                </h4>
                            )}

                            {invoice?.contract?.purchaseOrderInfluencer && (
                                <h4>
                                    <strong>Influencer PO:</strong> {invoice?.contract?.purchaseOrderInfluencer ?? '-'}
                                </h4>
                            )}

                            {!invoice.isPaid && (
                                <Row>
                                    <Button
                                        className="col-12 mt-3"
                                        size="lg"
                                        disabled={invoice.isVerified}
                                        onClick={() => setConfirmModalData({ showModal: true })}
                                    >
                                        APPROVE
                                    </Button>

                                    <Button
                                        className="col-12 mt-3"
                                        size="lg"
                                        variant="outline-primary"
                                        disabled={invoice.isRejected}
                                        onClick={() => setModalData({ showModal: true })}
                                    >
                                        REJECT
                                    </Button>
                                </Row>
                            )}
                        </Col>

                        <Col md={8} className="d-flex justify-content-center align-items-center">
                            <CustomFancybox files={[invoice]} />
                        </Col>
                    </Row>
                </Form>
            </Card>
        </>
    );
}
